/**
 * Cart sharing utilities — encode/decode cart in a URL param.
 * Used for sharing carts between users via WhatsApp, native share, or copy link.
 *
 * Encoding: minimize cart payload to essential fields, JSON.stringify, btoa.
 * Decoding: atob → JSON.parse → returns array of cart items (or null on failure).
 */

const PARAM = 'cart';

/**
 * Compact a cart item to only the fields needed to recreate it.
 * Drops display-only fields (image, description, etc.) to keep URL short.
 */
const compactItem = (item) => ({
  i: item.id,
  s: item.size,
  q: item.quantity,
  t: item.selectedTradition || null,
  h: item.selectedHilos || null,
  c: item.selectedCottonType || null,
  cv: item.selectedColor?.value || null,
});

const expandItem = (c) => ({
  id: c.i,
  size: c.s,
  quantity: c.q || 1,
  selectedTradition: c.t || undefined,
  selectedHilos: c.h || undefined,
  selectedCottonType: c.c || undefined,
  selectedColor: c.cv ? { value: c.cv } : undefined,
});

const safeBase64Encode = (str) => {
  // Use URL-safe base64 (no +/=)
  const utf8 = unescape(encodeURIComponent(str));
  return btoa(utf8).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const safeBase64Decode = (str) => {
  let s = str.replace(/-/g, '+').replace(/_/g, '/');
  while (s.length % 4) s += '=';
  const decoded = atob(s);
  return decodeURIComponent(escape(decoded));
};

export const encodeCartToUrl = (cart, baseUrl) => {
  if (!Array.isArray(cart) || cart.length === 0) return null;
  const compact = cart.map(compactItem);
  const payload = safeBase64Encode(JSON.stringify(compact));
  const url = baseUrl || (typeof window !== 'undefined' ? window.location.origin + window.location.pathname : '');
  return `${url}?${PARAM}=${payload}`;
};

export const decodeCartFromUrl = (urlOrSearch) => {
  try {
    let search = urlOrSearch;
    if (typeof search === 'undefined' && typeof window !== 'undefined') {
      search = window.location.search;
    }
    const params = new URLSearchParams(search.startsWith('?') ? search : `?${search.split('?')[1] || ''}`);
    const raw = params.get(PARAM);
    if (!raw) return null;
    const json = safeBase64Decode(raw);
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) return null;
    return arr.map(expandItem);
  } catch (e) {
    return null;
  }
};

export const stripCartParam = () => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (url.searchParams.has(PARAM)) {
    url.searchParams.delete(PARAM);
    window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
  }
};
